/**
 * 为二维 BasisU 容器选择不超过最长边上限的 mip，重建后再交给转码器。
 * 不解码像素；ETC1S 保留全局码本，仅移除高分辨率 imageDesc 和 mip 数据。
 */
export function selectMeshKtx2Mips(data: ArrayBuffer, maxSize: number): ArrayBuffer {
  if (!(maxSize > 0) || (Number.isFinite(maxSize) && !Number.isInteger(maxSize)))
    throw new RangeError('KTX2 maxSize 必须是正整数或 Infinity')
  const view = new DataView(data)
  let width = view.getUint32(20, true)
  let height = view.getUint32(24, true)
  const levelCount = view.getUint32(40, true)
  if (width <= maxSize && height <= maxSize) return data
  if (levelCount > 1 + Math.floor(Math.log2(Math.max(width, height))))
    throw new Error('KTX2 mip 层数无效')
  let firstLevel = 0
  while (firstLevel < levelCount && (width > maxSize || height > maxSize)) {
    firstLevel++
    width = Math.max(1, Math.floor(width / 2))
    height = Math.max(1, Math.floor(height / 2))
  }
  if (firstLevel >= levelCount)
    throw new Error(`KTX2 缺少满足 maxSize=${maxSize} 的 mip，请预先生成 mip 链或提高上限`)

  const uint64 = (offset: number): number => {
    const value = view.getBigUint64(offset, true)
    if (value > BigInt(Number.MAX_SAFE_INTEGER)) throw new Error('KTX2 字节范围超限')
    return Number(value)
  }
  const slice = (offset: number, length: number): Uint8Array => {
    if (offset < 0 || length < 0 || offset + length > data.byteLength)
      throw new Error('KTX2 数据范围无效')
    return new Uint8Array(data, offset, length)
  }
  slice(80, levelCount * 24)
  const scheme = view.getUint32(44, true)
  if (![0, 1, 2].includes(scheme)) throw new Error('KTX2 不支持此超压缩方案的 mip 选择')
  const dfd = slice(view.getUint32(48, true), view.getUint32(52, true))
  const kvd = slice(view.getUint32(56, true), view.getUint32(60, true))
  let sgd = slice(uint64(64), uint64(72))
  if (scheme === 1) {
    // BasisLZ SGD: 20-byte header, one 20-byte imageDesc per 2D mip, then codebooks.
    const codebookOffset = 20 + levelCount * 20
    if (sgd.byteLength < codebookOffset) throw new Error('KTX2 BasisLZ imageDesc 范围无效')
    const selected = new Uint8Array(sgd.byteLength - firstLevel * 20)
    selected.set(sgd.subarray(0, 20))
    selected.set(sgd.subarray(20 + firstLevel * 20, codebookOffset), 20)
    selected.set(sgd.subarray(codebookOffset), 20 + (levelCount - firstLevel) * 20)
    sgd = selected
  }
  const levels = Array.from({ length: levelCount - firstLevel }, (_, index) => {
    const entry = 80 + (index + firstLevel) * 24
    return {
      bytes: slice(uint64(entry), uint64(entry + 8)),
      uncompressedBytes: view.getBigUint64(entry + 16, true),
      offset: 0,
    }
  })
  const align = (value: number, multiple: number) => Math.ceil(value / multiple) * multiple
  const dfdOffset = 80 + levels.length * 24
  const kvdOffset = align(dfdOffset + dfd.byteLength, 4)
  const sgdOffset = align(kvdOffset + kvd.byteLength, 8)
  let size = sgdOffset + sgd.byteLength
  // KTX2 将最小 mip 放在前面；未超压缩 UASTC 的块按 16 字节对齐。
  for (let index = levels.length - 1; index >= 0; index--) {
    size = align(size, scheme === 0 ? 16 : 1)
    levels[index].offset = size
    size += levels[index].bytes.byteLength
  }
  const result = new Uint8Array(size)
  result.set(slice(0, 80))
  const header = new DataView(result.buffer)
  header.setUint32(20, width, true)
  header.setUint32(24, height, true)
  header.setUint32(40, levels.length, true)
  header.setUint32(48, dfdOffset, true)
  header.setUint32(56, kvd.byteLength ? kvdOffset : 0, true)
  header.setBigUint64(64, BigInt(sgd.byteLength ? sgdOffset : 0), true)
  header.setBigUint64(72, BigInt(sgd.byteLength), true)
  result.set(dfd, dfdOffset)
  result.set(kvd, kvdOffset)
  result.set(sgd, sgdOffset)
  for (const [index, level] of levels.entries()) {
    const entry = 80 + index * 24
    header.setBigUint64(entry, BigInt(level.offset), true)
    header.setBigUint64(entry + 8, BigInt(level.bytes.byteLength), true)
    header.setBigUint64(entry + 16, level.uncompressedBytes, true)
    result.set(level.bytes, level.offset)
  }
  return result.buffer
}
